import {
  AssignmentLimitModel,
  AssignmentRuleModel,
  TeamMemberChangeModel,
  TeamMemberNodeModel,
  TeamModel
} from '../models/team.model';
import {
  AssignmentLimitUiModel,
  AssignmentRuleUiModel,
  TeamMemberChangeUiModel,
  TeamMemberUiModel,
  TeamUiModel
} from '../ui-models/team.ui-model';

class TeamMapper {
  public static mapTeamToUI(model: TeamModel): TeamUiModel {
    return {
      id: model.id,
      name: model.name,
      grants: (model.accessControl ?? []).map((grant) => ({
        module: grant.module,
        filters: (grant.enforcedFilters ?? []).map((filter) => ({
          key: filter.key,
          values: filter.value ?? []
        })),
        // A grant with no filters is an unconditional org-wide browse — the
        // most permissive thing a team can hold. Flagged so the UI can say so
        // rather than rendering what looks like an empty row.
        isUnconditional: (grant.enforcedFilters ?? []).length === 0
      })),
      isArchived: Boolean(model.isArchived),
      createdAt: model.createdAt ? new Date(model.createdAt) : undefined,
      updatedAt: model.updatedAt ? new Date(model.updatedAt) : undefined
    };
  }

  public static mapTeamToUIList(models: TeamModel[]): TeamUiModel[] {
    return (models ?? []).map(TeamMapper.mapTeamToUI);
  }

  /**
   * Maps the tree the BFF assembled.
   *
   * `isOrphan` marks a node that sits at the top without being a manager —
   * meaning its `reportsTo` pointed at someone absent from the result. The BFF
   * returns those as roots rather than dropping them, and this carries that
   * distinction through so the UI can show it as a data problem instead of
   * quietly presenting them as a second manager.
   */
  public static mapMemberToUI(model: TeamMemberNodeModel, isRoot = true): TeamMemberUiModel {
    return {
      userId: model.userId ?? model.id,
      role: model.role,
      name: model.user?.name ?? model.user?.email ?? model.userId ?? model.id,
      email: model.user?.email,
      image: model.user?.image,
      teamNames: (model.team ?? []).map((team) => team.name),
      teamIds: (model.team ?? []).map((team) => team.id),
      reportsToId: model.reportsTo ?? undefined,
      children: (model.children ?? []).map((child) => TeamMapper.mapMemberToUI(child, false)),
      isOrphan: isRoot && Boolean(model.reportsTo)
    };
  }

  public static mapTreeToUI(models: TeamMemberNodeModel[]): TeamMemberUiModel[] {
    return (models ?? []).map((node) => TeamMapper.mapMemberToUI(node, true));
  }

  public static mapChangeToUI(model: TeamMemberChangeModel): TeamMemberChangeUiModel {
    return {
      id: model.id,
      changeType: model.changeType,
      before: model.before,
      after: model.after,
      changedByName:
        model.changedByUser?.name ?? model.changedByUser?.email ?? model.changedBy,
      createdAt: new Date(model.createdAt)
    };
  }

  public static mapChangeToUIList(models: TeamMemberChangeModel[]): TeamMemberChangeUiModel[] {
    return (models ?? []).map(TeamMapper.mapChangeToUI);
  }

  public static mapLimitToUI(model: AssignmentLimitModel): AssignmentLimitUiModel {
    return {
      id: model.id,
      role: model.role,
      maxActiveLeads: model.maxActiveLeads,
      // null means unlimited, and so does a missing row. Rendering either as 0
      // would say "this role may hold no leads", the opposite of true.
      isUnlimited: model.maxActiveLeads === null || model.maxActiveLeads === undefined
    };
  }

  public static mapLimitToUIList(models: AssignmentLimitModel[]): AssignmentLimitUiModel[] {
    return (models ?? []).map(TeamMapper.mapLimitToUI);
  }

  public static mapRuleToUI(model: AssignmentRuleModel): AssignmentRuleUiModel {
    const criteria = model.criteria ?? {};
    return {
      id: model.id,
      name: model.name,
      criteria,
      criteriaCount: Object.keys(criteria).length,
      assigneeIds: model.assigneeIds ?? [],
      assigneeNames: (model.assignees ?? []).map(
        (user) => user.name ?? user.email ?? user.id
      ),
      skipCap: Boolean(model.skipCap),
      isActive: Boolean(model.isActive),
      // A rule with no criteria is a legitimate catch-all, not an unfinished
      // row — the most specific matching rule wins, so this one takes whatever
      // nothing narrower claimed.
      isCatchAll: Object.keys(criteria).length === 0
    };
  }

  public static mapRuleToUIList(models: AssignmentRuleModel[]): AssignmentRuleUiModel[] {
    return (models ?? []).map(TeamMapper.mapRuleToUI);
  }
}

export default TeamMapper;
